import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { RenewalsClient } from '@acorn-portal/shared/src/services/renewalsApi';
import { useNavigate } from 'react-router';
import { PoliciesClient } from '../services/api';
import { INetworkError } from '../services/interface/INetworkError';
import { IFullPolicyState, updateFullPolicyDetails } from '../store/fullPolicy.slice';
import { updateRenewalDetails } from '../store/renewal.slice';
import { ROUTE_PATHS } from '../utils/Routes';
import { useAppDispatch } from './reduxHook';
import transformFullPolicyToState from '../utils/transform-full-policy-to-state';

/**
 * Custom hook to get renewal details and dispatch to Redux store
 * Provides two main functions:
 * 1. getRenewalRating - to get renewal details when full policy details are already available
 * 2. getPolicyAndRateRenewal - to first fetch full policy details, then get renewal details
 */
const useGetRenewalDetails = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	/**
	 * Exported function to get the renewal rating details using policyToken, policyNumber and transactionId
	 * Used in situations where full policy details are already available e.g. on Policy Details page
	 * Dispatches renewal details to Redux store
	 *
	 */
	const getRenewalRating = async (
		policyToken: string,
		policyNumber: string,
		renewalStartDate: string,
		transactionId: string,
	): Promise<void> => {
		try {
			const client = new RenewalsClient(undefined, API);
			const renewalDetails = await client.getRenewalRating(policyToken, transactionId);
			const selectedAddons =
				renewalDetails.renewalRatingResponse?.addOns
					?.filter((addon) => addon.selected)
					?.map((addon) => ({
						underwrittenDataPointId: addon.underwrittenDataPointId,
						addOnCode: addon.addOnCode,
					})) ?? [];
			// Dispatch to Redux
			dispatch(
				updateRenewalDetails({
					vehicleDetails: renewalDetails.vehicleDetails,
					policyDetails: renewalDetails.policyDetails,
					drivers: renewalDetails.drivers,
					renewalStartDate,
					renewalRatingResponse: renewalDetails.renewalRatingResponse,
					policyReference: policyNumber,
					ncbProtection: renewalDetails.renewalRatingResponse?.coverDetails?.ncbDetails?.isNCBSelected ?? false,
					addons: selectedAddons,
				}),
			);
		} catch (error) {
			const networkError = error as INetworkError;
			if ('status' in networkError && networkError.status === 409) {
				navigate(ROUTE_PATHS.policyRenewalRefer);
			} else {
				navigate(ROUTE_PATHS.error);
			}
		}
	};

	/**
	 * Internal function to assist getPolicyAndRateRenewal
	 * Dispatches full policy details to Redux store and returns FullPolicy object
	 * The full policy details are needed to get the transactionId and start date for the renewal rating call
	 */
	const fetchPolicyDetails = async (policyToken: string): Promise<IFullPolicyState | null> => {
		try {
			const policiesClient = new PoliciesClient(undefined, API);
			const result = await policiesClient.getPolicy(policyToken);
			const transformedResult = transformFullPolicyToState(result);
			dispatch(updateFullPolicyDetails(transformedResult));
			return transformedResult;
		} catch (err) {
			console.error('Failed to fetch policy details:', err);
			navigate(ROUTE_PATHS.error);
			return null;
		}
	};

	/**
	 * 	Exported function - calls fetchPolicyDetails first to get transactionId, then fetchRenewalRating to get renewal details
	 * 	Used in situations where full policy details are needed prior to getting renewal details e.g. on Policy List pages where we only have limited policy info
	 *  Accepts optional callback to be called after successful data fetch - useful for navigation after data is ready
	 */
	const getPolicyAndRateRenewal = async (
		policyToken: string,
		policyNumber: string,
		callback?: () => void,
	): Promise<void> => {
		// First fetch full policy details to get transactionId
		const fullPolicy = await fetchPolicyDetails(policyToken);
		const transactionId = fullPolicy?.renewalDetails?.transactionId ?? '';
		const startDate = (fullPolicy?.renewalDetails?.renewalCoverDetails?.startDate as string) ?? '';
		await getRenewalRating(policyToken, policyNumber, startDate, transactionId);
		if (callback) callback();
	};

	return { getPolicyAndRateRenewal, getRenewalRating };
};

export default useGetRenewalDetails;
